import { create } from 'zustand'
import fetcher from "../fetcher";
import {useSavedDealershipsStore} from "./savedDealershipsStore";

// The provider caps how many dealer ids we can send per request.
const MAX_DEALER_IDS = 50

// Dealer ids to send: the user's saved dealership ids when "My Dealers" is
// active, otherwise none. Resolved at request time so the filter is always
// current without storing a duplicate in `filters`.
//
// When a make is being searched we can only send dealers that carry it (and at
// most 50). A dealer with an unknown/empty `makes` list can't be classified, so
// it's kept — but known carriers are prioritized so the 50 slots go to them
// first; dealers whose makes are known and DON'T include the make are dropped.
const resolveDealerIds = (dealerMode, make, hiddenDealerIds = []) => {
    if (dealerMode !== 'my') return []

    const hidden = new Set((hiddenDealerIds || []).map(String))
    // Dealers the user has toggled off (eye-off in the saved panel) are excluded
    // from the "My Dealers" filter without being removed from the saved network.
    const visible = useSavedDealershipsStore.getState().saved
        .filter((d) => !hidden.has(String(d.external_id)))

    if (!make) {
        return visible.slice(0, MAX_DEALER_IDS).map((d) => String(d.external_id))
    }

    const target = String(make).toLowerCase()
    const carries = (d) => (d.makes || []).some((m) => String(m).toLowerCase() === target)
    const unknown = (d) => (d.makes || []).length === 0

    const prioritized = [...visible.filter(carries), ...visible.filter(unknown)]
    return prioritized.slice(0, MAX_DEALER_IDS).map((d) => String(d.external_id))
}

export const ON_LOT = "On Lot"
export const IN_TRANSIT = "In Transit"
export const BUILD = "Build"
export const ALL = "All"

export const SEAT_CAPACITY_OPTIONS = ["2", "4", "5", "6", "7", "8"]

export const SORT_OPTIONS = [
    {label: 'Location', value: 'location'},
    {label: 'Recently Added', value: 'last_seen'},
    {label: 'MSRP: Low to High', value: 'msrp_asc'},
    {label: 'MSRP: High to Low', value: 'msrp_desc'},
    {label: 'Mileage', value: 'mileage'},
]

// Sort options for the active dealer mode. "By location" needs a single search
// center, which My Dealers doesn't have, so it's dropped there; All Dealers keeps
// the full list. The first entry is the mode's default sort.
export const sortOptionsForMode = (dealerMode) => {
    if (dealerMode !== 'my') return SORT_OPTIONS
    return SORT_OPTIONS.filter((o) => o.value !== 'location')
}

// Keep a selected sort valid for the target mode: switching to My Dealers makes
// 'location' unavailable, so fall back to the mode's default rather than leaving an
// option the dropdown can't show.
export const coerceSortForMode = (sortBy, dealerMode) => {
    const options = sortOptionsForMode(dealerMode)
    return options.some((o) => o.value === sortBy) ? sortBy : options[0].value
}

// When these filters change new listings are fetched
const initialFilters = {
    // Legacy single availability radio, kept as the unfiltered fallback ("All").
    // The sidebar drives availability through the multi-select
    // `availability_statuses` below, which takes precedence server-side.
    availability: ALL,
    // Selected availability statuses as Visor codes (stock/transit/build) — the
    // same values the `availability_status_options` facet returns, OR'd
    // server-side. Empty = all statuses (On Lot + In Transit + Build). Default is
    // All (empty) so incoming inventory isn't hidden by default and the
    // availability facet counts stay in agreement with the result list — the
    // backend's `availability` default is likewise All. A non-empty default (e.g.
    // On Lot only) would hide yet still count the excluded statuses, e.g. a future
    // model year whose only matches are In Transit.
    availability_statuses: [],

    searchTags: {
        // year: "2026",
        // make: "Honda",
        // model: "Accord",
        // trim: "Touring",
    },
    // BaseCar pk for an exact-trim search, sent to the backend as `car`. Set
    // when picking an exact trim (search suggestion / calc drawer); null
    // otherwise, in which case the backend just filters by make/model/trim.
    car: null,
    // Default dealerMode is 'my', where 'location' isn't offered; default to its
    // first available sort option.
    sort_by: sortOptionsForMode('my')[0].value,
    seats: [],
    location: {
        zipcode: '90036',
        latitude: null,
        longitude: null,
        // Search radius (miles) shown in the results-header "Within X miles of Y"
        // control. Sent with the location; ignored server-side if unsupported.
        radius: 150,
    },

    general_exterior_color_filters: [],
    general_interior_color_filters: [],

    specific_exterior_color_filters: [],
    specific_interior_color_filters: [],

    // Installed option-package codes selected in the sidebar.
    options: [],

    // Selected model years (multi-select; OR'd into the Visor `year` param).
    years: [],
    // Selected inventory types — new/used/certified (multi-select; OR'd into the
    // Visor `inventory_type` param).
    inventory_types: [],
    // Visor `version` facet values selected in the sidebar (OR'd server-side).
    versions: [],
    // Visor `engine` facet values selected in the sidebar (OR'd server-side).
    engines: [],
    // Body styles selected in the sidebar (OR'd server-side).
    body_types: [],

    features: []
}

const initialOptions = {
    option_packages: [],
    year_options: [],
    inventory_type_options: [],
    availability_status_options: [],
    version_options: [],
    engine_options: [],
    body_type_options: [],
    features: [],
    seat_capacity_options: SEAT_CAPACITY_OPTIONS,

    general_exterior_color_options: [],
    general_interior_color_options: [],

    specific_exterior_color_options: [],
    specific_interior_color_options: [],
}

// Maps a sidebar filter field (the thing the user toggled) to the Visor facet the
// backend can skip recomputing, plus the response options list to preserve. A
// facet's counts are computed ignoring its own selection, so toggling one of its
// values can't change its own counts — we tell the backend to `skip_facets` it
// (it comes back empty) and keep the counts we're already showing. Every OTHER
// facet does change and is refreshed from the response as usual.
export const FACET_TOGGLE_MAP = {
    versions:                        {facet: 'version',             optionsKey: 'version_options'},
    years:                           {facet: 'year',                optionsKey: 'year_options'},
    inventory_types:                 {facet: 'inventory_type',      optionsKey: 'inventory_type_options'},
    availability_statuses:           {facet: 'availability_status', optionsKey: 'availability_status_options'},
    engines:                         {facet: 'engine',              optionsKey: 'engine_options'},
    body_types:                      {facet: 'body_type',           optionsKey: 'body_type_options'},
    seats:                          {facet: 'seating_capacity',    optionsKey: 'seat_capacity_options'},
    general_exterior_color_filters:  {facet: 'base_exterior_color', optionsKey: 'general_exterior_color_options'},
    general_interior_color_filters:  {facet: 'base_interior_color', optionsKey: 'general_interior_color_options'},
    specific_exterior_color_filters: {facet: 'exterior_color',      optionsKey: 'specific_exterior_color_options'},
    specific_interior_color_filters: {facet: 'interior_color',      optionsKey: 'specific_interior_color_options'},
    options:                         {facet: 'options_packages',    optionsKey: 'option_packages'},
    features:                        {facet: 'features',            optionsKey: 'features'},
}

// The grid is paginated (30 at a time), but the map needs to plot every
// dealer at once — otherwise it only shows the closest dealer's page. The map
// fetches its own, much larger batch in a single request.
const MAP_ROWS = 1000

export const useListingsStore = create((set, get) => ({
    listings: [],
    loading: false,
    count: 0,
    start: 0,
    rows: 30,

    // Separate, larger dataset used only by the map view.
    mapListings: [],
    mapLoading: false,

    viewMode: 'grid',
    setViewMode: (mode) => {
        set({viewMode: mode})
        // Populate the map's dataset the first time it's shown / re-shown.
        if (mode === 'map') get().fetchMapListings()
    },

    // "My Dealers" (saved) vs "All Dealers". Default 'my' so saved dealerships
    // filter the search by default; the DealerToggle syncs the concrete ids into
    // filters.dealers. Persisted so it survives other filter changes.
    dealerMode: 'my',
    setDealerMode: (mode) => {
        // Reset pagination too — same reason as setFilters. Coerce the sort in the
        // same update so a now-invalid option ('location' under My Dealers) is
        // corrected before the single refetch, without a second request.
        const {filters} = get()
        const sort_by = coerceSortForMode(filters.sort_by, mode)
        set({dealerMode: mode, start: 0, filters: {...filters, sort_by}})
        get().fetchListings()
        if (get().viewMode === 'map') get().fetchMapListings()
    },

    // Saved-dealer external_ids the user has toggled off (eye-off) in the panel.
    // These stay saved but are dropped from the "My Dealers" filter.
    hiddenDealerIds: [],
    toggleDealerVisibility: (externalId) => {
        const id = String(externalId)
        const hidden = get().hiddenDealerIds
        const next = hidden.includes(id)
            ? hidden.filter((x) => x !== id)
            : [...hidden, id]
        set({hiddenDealerIds: next, start: 0})
        get().fetchListings()
        if (get().viewMode === 'map') get().fetchMapListings()
    },

    // Toggle single-dealer focus via the same hiddenDealerIds the eye drives:
    // if the target is already the sole visible saved dealer, show all (clear
    // hidden); otherwise hide every other saved dealer. Reads `saved` at call
    // time to avoid stale closures.
    toggleShowOnlyDealer: (externalId) => {
        const target = String(externalId)
        const available = useSavedDealershipsStore.getState().saved.map((d) => String(d.external_id))
        const visible = available.filter((id) => !get().hiddenDealerIds.includes(id))
        const alreadyOnly = visible.length === 1 && visible[0] === target
        const next = alreadyOnly ? [] : available.filter((id) => id !== target)
        set({hiddenDealerIds: next, start: 0})
        get().fetchListings()
        if (get().viewMode === 'map') get().fetchMapListings()
    },

    filters: initialFilters,
    options: initialOptions,

    // Last zipcode used as the search origin — kept for display (the Sort by
    // "Location (zip)" label) even after a map pan switches the search to
    // coordinates and clears filters.location.zipcode.
    displayZipcode: initialFilters.location.zipcode,

    // `toggledField` (optional) is the sidebar filter field the user just toggled
    // (a key of FACET_TOGGLE_MAP). Pass it ONLY for a single facet-value toggle so
    // that facet's counts are skipped/kept; omit it for anything that changes the
    // cohort for every facet (make/model, dealers, location, sort, clear/reset).
    setFilters: (newFilters, shouldResetOptions, toggledField) => {
        const oldFilters = get().filters
        // Reset pagination to the first page — otherwise a leftover offset (e.g.
        // 30 after "load more") is reused for the new, possibly smaller, result
        // set and returns nothing.
        const update = {filters: {...oldFilters, ...newFilters}, start: 0}
        const newZip = newFilters?.location?.zipcode
        if (newZip) update.displayZipcode = newZip
        set(update)
        get().fetchListings(shouldResetOptions, false, toggledField)
        // Keep the map in sync when it's the active view (e.g. sidebar filter
        // changes, or a pan that moves the search center).
        if (get().viewMode === 'map') get().fetchMapListings()
    },

    setPagination: (rowsAndStart) => {
        set(rowsAndStart)
        get().fetchListings(false, true)
    },

    search: "",
    setSearch: (value) => set(() => ({search: value})),

    // Search-as-you-type suggestions (year/make/model) for the search bar.
    suggestions: [],
    suggestionsQuery: '',
    fetchSuggestions: async (q) => {
        set({suggestionsQuery: q})
        const response = await fetcher.suggestListings(q)
        // Ignore out-of-order responses from earlier keystrokes.
        if (get().suggestionsQuery !== q) return
        set({suggestions: response?.data || []})
    },
    clearSuggestions: () => set({suggestions: [], suggestionsQuery: ''}),

    searchCars: async (params) => {
        set({loading: true})
        const response = await fetcher.searchCars(params)
        if (response?.data) {
            // console.log(response?.data)
        }
        set({loading: false})
    },

    fetchListings: async (shouldResetOptions, shouldAddToList = false, toggledField = null) => {
        set({loading: true})
        let filters = structuredClone(get().filters)
        const searchTags = filters.searchTags
        filters = {...filters, ...searchTags}

        delete filters.searchTags

        // Toggling a facet value can't change that facet's own (self-excluded)
        // counts, so ask the backend to skip recomputing it and keep the counts we
        // already show for it. Only ever the one facet the user just toggled.
        const skip = toggledField ? FACET_TOGGLE_MAP[toggledField] : null

        const requestData = {
            ...filters,
            dealers: resolveDealerIds(get().dealerMode, get().filters.searchTags.make, get().hiddenDealerIds),
            start: get().start,
            rows: get().rows,
        }
        if (skip) requestData.skip_facets = [skip.facet]

        const response = await fetcher.getListings(requestData)
        if (response?.data) {
            const { listings: newListings, count, options } = response?.data

            // Always rebuild the sidebar's facet option lists from the latest
            // response so each filter reflects the current search (e.g. picking
            // Condition = Used refreshes the Version list to the used versions).
            // Selections live in `filters.*`, separate from these option lists,
            // so refreshing options never drops a user's checked values.
            let newOptions = options ?? get().options
            // The skipped facet comes back empty — keep the counts we already have.
            if (skip && newOptions) {
                newOptions = {...newOptions, [skip.optionsKey]: get().options[skip.optionsKey]}
            }

            let responseData = {
                listings: newListings,
                loading: false,
                count: count,
                options: newOptions,
            }

            if (shouldAddToList) {
                responseData.listings = get().listings.concat(newListings)
            }

            set(responseData)
        }
    },

    // Fetch a single large batch for the map so every dealer is plotted (not
    // just the closest dealer's 30-item page). Same filters as the grid, but
    // from the top with a high row count.
    fetchMapListings: async () => {
        set({mapLoading: true})
        let filters = structuredClone(get().filters)
        const searchTags = filters.searchTags
        filters = {...filters, ...searchTags}
        delete filters.searchTags

        const requestData = {
            ...filters,
            dealers: resolveDealerIds(get().dealerMode, get().filters.searchTags.make, get().hiddenDealerIds),
            start: 0,
            rows: MAP_ROWS,
        }
        const response = await fetcher.getListings(requestData)
        if (response?.data) {
            set({mapListings: response.data.listings || [], mapLoading: false})
        } else {
            set({mapLoading: false})
        }
    }
}))